import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/shared/States";
import { Avatar } from "@/components/shared/Badges";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { UserCircle, Camera, Trash2, Lock, Bell, Loader2, ShieldCheck, Wand2, BellRing, BellOff } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { isPushSupported, getPushSubscriptionState, enablePushNotifications, disablePushNotifications, sendTestPush } from "@/lib/push";

const NOTIFICATION_OPTIONS = [
  ["email_notifications", "Email notifications", "Receive important account and activity emails."],
  ["task_reminders", "Task reminders", "Get reminded about upcoming and overdue tasks."],
  ["mentions", "Mentions & replies", "Notify me when a teammate mentions or replies to me."],
  ["weekly_digest", "Weekly digest", "A Monday summary of your pipeline and performance."],
  ["product_updates", "Product updates", "News about new Aurelo features and improvements."],
];

const DEFAULT_PREFS = {
  email_notifications: true,
  task_reminders: true,
  mentions: true,
  weekly_digest: false,
  product_updates: true,
};

export default function Account() {
  const { user, updateProfile, changePassword } = useAuth();
  const fileRef = useRef(null);

  // Profile state
  const [form, setForm] = useState({ name: "", title: "", email: "", phone: "" });
  const [avatar, setAvatar] = useState(null);
  const [savingProfile, setSavingProfile] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);

  // AI avatar generation
  const [aiDialogOpen, setAiDialogOpen] = useState(false);
  const [aiPrompt, setAiPrompt] = useState("");
  const [generatingAvatar, setGeneratingAvatar] = useState(false);

  // Password state
  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm: "" });
  const [savingPw, setSavingPw] = useState(false);

  // Notifications state
  const [prefs, setPrefs] = useState(DEFAULT_PREFS);
  const [savingPrefs, setSavingPrefs] = useState(false);
  const [pushState, setPushState] = useState("not_subscribed");
  const [pushBusy, setPushBusy] = useState(false);

  useEffect(() => {
    if (user) {
      setForm({ name: user.name || "", title: user.title || "", email: user.email || "", phone: user.phone || "" });
      setAvatar(user.avatar || null);
      setPrefs({ ...DEFAULT_PREFS, ...(user.notification_prefs || {}) });
    }
  }, [user]);

  useEffect(() => {
    getPushSubscriptionState().then(setPushState);
  }, []);

  const onPickFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting the same file
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Please choose an image file");
    if (file.size > 8 * 1024 * 1024) return toast.error("Image is too large (max 8MB)");
    setUploadingAvatar(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload/avatar", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setAvatar(data.avatar);
      toast.success("Photo uploaded — click Save to apply");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't upload that image");
    } finally {
      setUploadingAvatar(false);
    }
  };

  const generateAiAvatar = async () => {
    if (!aiPrompt.trim()) return toast.error("Describe the avatar you'd like, e.g. \"friendly cartoon fox\"");
    setGeneratingAvatar(true);
    try {
      const { data } = await api.post("/ai/generate-image", { prompt: aiPrompt.trim(), target: "avatar" });
      setAvatar(data.url);
      setAiDialogOpen(false);
      setAiPrompt("");
      toast.success("Avatar generated — click Save to apply");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Image generation failed");
    } finally {
      setGeneratingAvatar(false);
    }
  };

  const togglePush = async () => {
    setPushBusy(true);
    try {
      if (pushState === "subscribed") {
        await disablePushNotifications();
        setPushState("not_subscribed");
        toast.success("Browser notifications turned off");
      } else {
        await enablePushNotifications();
        setPushState("subscribed");
        toast.success("Browser notifications turned on");
      }
    } catch (err) {
      toast.error(err?.message || "Couldn't update notification settings");
    } finally {
      setPushBusy(false);
    }
  };

  const testPush = async () => {
    try {
      await sendTestPush();
      toast.success("Test notification sent — check your browser");
    } catch {
      toast.error("Couldn't send test notification");
    }
  };

  const saveProfile = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    if (!form.email.trim()) return toast.error("Email is required");
    setSavingProfile(true);
    try {
      await updateProfile({
        name: form.name.trim(),
        title: form.title.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        avatar: avatar || "",
      });
      toast.success("Profile updated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't update profile");
    } finally {
      setSavingProfile(false);
    }
  };

  const savePassword = async () => {
    if (pw.new_password.length < 6) return toast.error("New password must be at least 6 characters");
    if (pw.new_password !== pw.confirm) return toast.error("New passwords don't match");
    setSavingPw(true);
    try {
      await changePassword(pw.current_password, pw.new_password);
      toast.success("Password changed");
      setPw({ current_password: "", new_password: "", confirm: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't change password");
    } finally {
      setSavingPw(false);
    }
  };

  const savePrefs = async (next) => {
    setPrefs(next);
    setSavingPrefs(true);
    try {
      await updateProfile({ notification_prefs: next });
      toast.success("Preferences saved");
    } catch {
      toast.error("Couldn't save preferences");
    } finally {
      setSavingPrefs(false);
    }
  };

  return (
    <div data-testid="account-page">
      <PageHeader title="My Account" subtitle="Manage your personal profile and preferences" icon={UserCircle} />

      <Tabs defaultValue="profile">
        <TabsList>
          <TabsTrigger value="profile" data-testid="account-tab-profile">Profile</TabsTrigger>
          <TabsTrigger value="password" data-testid="account-tab-password">Password</TabsTrigger>
          <TabsTrigger value="notifications" data-testid="account-tab-notifications">Notifications</TabsTrigger>
        </TabsList>

        {/* PROFILE */}
        <TabsContent value="profile" className="mt-4">
          <div className="rounded-2xl bg-white border border-slate-200 p-6 max-w-2xl">
            {/* Avatar */}
            <div className="flex items-center gap-5">
              <div className="relative">
                <Avatar name={form.name} src={avatar} size={88} />
                {uploadingAvatar && (
                  <div className="absolute inset-0 rounded-full bg-black/40 flex items-center justify-center">
                    <Loader2 className="h-5 w-5 text-white animate-spin" />
                  </div>
                )}
                <button
                  type="button"
                  data-testid="avatar-upload-trigger"
                  onClick={() => fileRef.current?.click()}
                  className="absolute -bottom-1 -right-1 h-8 w-8 rounded-full aurelo-gradient text-white flex items-center justify-center shadow-md ring-2 ring-white hover:opacity-90 transition-opacity"
                  aria-label="Change profile picture"
                >
                  <Camera className="h-4 w-4" />
                </button>
                <input ref={fileRef} type="file" accept="image/*" className="hidden" data-testid="avatar-file-input" onChange={onPickFile} />
              </div>
              <div>
                <div className="text-sm font-semibold text-slate-900">Profile picture</div>
                <p className="text-xs text-slate-500 mt-0.5 max-w-xs">PNG, JPG or WEBP, square looks best.</p>
                <div className="flex items-center gap-2 mt-2">
                  <Button size="sm" variant="outline" data-testid="avatar-choose-button" onClick={() => fileRef.current?.click()} disabled={uploadingAvatar}>
                    <Camera className="h-3.5 w-3.5 mr-1" /> Upload
                  </Button>
                  <Button size="sm" variant="outline" data-testid="avatar-generate-ai-button" onClick={() => setAiDialogOpen(true)}>
                    <Wand2 className="h-3.5 w-3.5 mr-1" /> Generate with AI
                  </Button>
                  {avatar && (
                    <Button size="sm" variant="ghost" className="text-red-600 hover:text-red-700 hover:bg-red-50" data-testid="avatar-remove-button" onClick={() => setAvatar(null)}>
                      <Trash2 className="h-3.5 w-3.5 mr-1" /> Remove
                    </Button>
                  )}
                </div>
              </div>
            </div>

            <Separator className="my-6" />

            {/* Fields */}
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="acc-name">Full name</Label>
                <Input id="acc-name" data-testid="profile-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" placeholder="Your name" />
              </div>
              <div>
                <Label htmlFor="acc-title">Job title</Label>
                <Input id="acc-title" data-testid="profile-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1" placeholder="e.g. Account Executive" />
              </div>
              <div>
                <Label htmlFor="acc-email">Email</Label>
                <Input id="acc-email" data-testid="profile-email-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1" placeholder="you@company.com" />
              </div>
              <div>
                <Label htmlFor="acc-phone">Phone</Label>
                <Input id="acc-phone" data-testid="profile-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1" placeholder="Optional" />
              </div>
            </div>

            <div className="mt-6">
              <Button data-testid="save-profile-button" className="aurelo-gradient text-white" onClick={saveProfile} disabled={savingProfile}>
                {savingProfile ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" />Saving…</> : "Save Changes"}
              </Button>
            </div>
          </div>
        </TabsContent>

        {/* PASSWORD */}
        <TabsContent value="password" className="mt-4">
          <div className="rounded-2xl bg-white border border-slate-200 p-6 max-w-lg">
            <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
              <Lock className="h-4 w-4 text-[#7C3AED]" /> Change password
            </div>
            <p className="text-xs text-slate-500 mt-1">Use at least 6 characters. Choose something you don't use elsewhere.</p>
            <div className="mt-4 space-y-4">
              <div>
                <Label htmlFor="pw-current">Current password</Label>
                <Input id="pw-current" data-testid="password-current-input" type="password" value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} className="mt-1" placeholder="Leave blank if you don't have one yet" />
              </div>
              <div>
                <Label htmlFor="pw-new">New password</Label>
                <Input id="pw-new" data-testid="password-new-input" type="password" value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} className="mt-1" />
              </div>
              <div>
                <Label htmlFor="pw-confirm">Confirm new password</Label>
                <Input id="pw-confirm" data-testid="password-confirm-input" type="password" value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} className="mt-1" />
              </div>
            </div>
            <div className="mt-6">
              <Button data-testid="save-password-button" className="aurelo-gradient text-white" onClick={savePassword} disabled={savingPw}>
                {savingPw ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" />Updating…</> : <><ShieldCheck className="h-4 w-4 mr-1.5" />Update Password</>}
              </Button>
            </div>
          </div>
        </TabsContent>

        {/* NOTIFICATIONS */}
        <TabsContent value="notifications" className="mt-4">
          <div className="rounded-2xl bg-white border border-slate-200 p-6 max-w-2xl">
            <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
              <Bell className="h-4 w-4 text-[#7C3AED]" /> Notification preferences
              {savingPrefs && <Loader2 className="h-3.5 w-3.5 animate-spin text-slate-400" />}
            </div>
            <p className="text-xs text-slate-500 mt-1">Choose what Aurelo can notify you about. Changes save instantly.</p>
            <div className="mt-4">
              {NOTIFICATION_OPTIONS.map(([key, label, desc]) => (
                <div key={key} className="flex items-center justify-between gap-4 py-3.5 border-b border-slate-100 last:border-0">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-slate-800">{label}</div>
                    <div className="text-xs text-slate-500">{desc}</div>
                  </div>
                  <Switch
                    data-testid={`notif-${key}`}
                    checked={!!prefs[key]}
                    onCheckedChange={(v) => savePrefs({ ...prefs, [key]: v })}
                  />
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-2xl bg-white border border-slate-200 p-6 max-w-2xl mt-4">
            <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
              {pushState === "subscribed" ? <BellRing className="h-4 w-4 text-[#7C3AED]" /> : <BellOff className="h-4 w-4 text-slate-400" />}
              Browser push notifications
            </div>
            <p className="text-xs text-slate-500 mt-1">
              {pushState === "unsupported"
                ? "Your browser doesn't support push notifications."
                : "Get a real desktop/browser alert when a teammate joins your workspace, right from this device."}
            </p>
            <div className="flex items-center gap-2 mt-4">
              <Button
                size="sm"
                variant={pushState === "subscribed" ? "outline" : "default"}
                className={pushState === "subscribed" ? "" : "aurelo-gradient text-white"}
                data-testid="push-toggle-button"
                disabled={pushBusy || pushState === "unsupported"}
                onClick={togglePush}
              >
                {pushBusy ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : null}
                {pushState === "subscribed" ? "Turn off" : "Turn on browser notifications"}
              </Button>
              {pushState === "subscribed" && (
                <Button size="sm" variant="ghost" data-testid="push-test-button" onClick={testPush}>Send test notification</Button>
              )}
            </div>
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={aiDialogOpen} onOpenChange={setAiDialogOpen}>
        <DialogContent data-testid="ai-avatar-dialog">
          <DialogHeader>
            <DialogTitle>Generate a profile picture with AI</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="ai-avatar-prompt">Describe the avatar</Label>
            <Textarea
              id="ai-avatar-prompt"
              data-testid="ai-avatar-prompt-input"
              value={aiPrompt}
              onChange={(e) => setAiPrompt(e.target.value)}
              placeholder='e.g. "friendly minimalist fox mascot, flat vector style, purple tones"'
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAiDialogOpen(false)} disabled={generatingAvatar}>Cancel</Button>
            <Button className="aurelo-gradient text-white" data-testid="ai-avatar-generate-button" onClick={generateAiAvatar} disabled={generatingAvatar}>
              {generatingAvatar ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" />Generating…</> : <><Wand2 className="h-4 w-4 mr-1.5" />Generate</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
